import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "react-toastify";
import Listtable from "components/common/ListTable";
import {
  createConsentTemplate,
  deleteConsentTemplate,
  duplicateConsentTemplate,
  getConsentTemplate,
  getConsentTemplateMeta,
  getConsentTemplates,
  setConsentTemplateStatus,
} from "api/consentTemplate";
import NewConsentDialog from "./NewConsentDialog";
import ConsentPreviewDialog from "./ConsentPreviewDialog";
import {
  activateBlockedReason,
  canActivate,
  CONSENT_STATUSES,
  formatDate,
  nextStatusFor,
  statusMeta,
} from "./consentTemplateModel";
import {
  ALL_SPECIALITIES_LABEL,
  consentDetailsPayload,
  simpleStarterDesign,
  specialityNames,
} from "./consentSimpleModel";
import { listSpecialities } from "api/patientHistoryMaster";
import { presetFieldsFor, withConsentFields } from "./consentSpecificFields";
import "./Consent.css";

const TABLE_HEADERS = [
  "Consent Name",
  "Specialities",
  "Consent Format",
  "Status",
  "Updated At",
  "Action",
];

const TABLE_KEYS = [
  "nameCell",
  "specialityCell",
  "typeLabel",
  "statusCell",
  "updatedAtLabel",
  "action",
];

const btnStyle = {
  width: "max-content",
  padding: "0.5rem 2rem",
  color: "#194AF5",
  bgcolor: "#E6EBFF",
  boxShadow: "none",
  borderRadius: "8px",
  ":hover": { bgcolor: "#E6EBFF", color: "#194AF5", boxShadow: "none" },
};

const apiMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const ConsentList = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({});
  const [specialityOptions, setSpecialityOptions] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
      setPageNumber(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    getConsentTemplateMeta()
      .then((response) => setMeta(response.data.data))
      .catch(() => setMeta({}));
  }, []);

  useEffect(() => {
    let active = true;
    listSpecialities()
      .then((list) => active && setSpecialityOptions(list))
      .catch(() => toast.error("Could not load specialities"));
    return () => {
      active = false;
    };
  }, []);

  const openPreview = useCallback(async (row) => {
    setBusy(true);
    try {
      const response = await getConsentTemplate(
        row._id,
        row.publishedVersion ? { version: row.publishedVersion } : {}
      );
      const payload = response.data.data;
      setPreviewing({
        template: payload.template,
        design: payload.version.design,
      });
    } catch (error) {
      toast.error(apiMessage(error, "Could not open the preview."));
    } finally {
      setBusy(false);
    }
  }, []);

  const changeStatus = useCallback(
    async (row, status) => {
      setBusy(true);
      try {
        const response = await setConsentTemplateStatus(row._id, status);
        toast.success(response.data.message || "Status updated");
        return true;
      } catch (error) {
        toast.error(apiMessage(error, "Could not change the status."));
        return false;
      } finally {
        setBusy(false);
      }
    },
    []
  );

  const fetchTemplates = useCallback(() => {
    setLoading(true);

    getConsentTemplates({
      page: pageNumber + 1,
      perPage: rowsPerPage,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
    })
      .then((response) => {
        const payload = response?.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];

        setRows(
          list.map((item) => {
            const status = statusMeta(item.status);
            const names = specialityNames(item, specialityOptions);

            return {
              ...item,
              nameCell: (
                <Box sx={{ pl: "1rem" }}>
                  <Typography variant="body2">{item.name}</Typography>
                </Box>
              ),
              /*
               * No specialities stored means every speciality — the convention
               * the Patient History masters already use, and what every consent
               * saved before the field existed reads back as.
               */
              specialityCell: names.length ? (
                <Typography variant="body2">{names.join(", ")}</Typography>
              ) : (
                <Chip size="small" variant="outlined" label={ALL_SPECIALITIES_LABEL} />
              ),
              statusCell: (
                <Chip size="small" label={status.label} color={status.color} />
              ),
              updatedAtLabel: formatDate(item.updatedAt),
              action: (
                <Box sx={{ display: "flex", gap: "0.25rem" }}>
                  <Tooltip title="Edit in builder">
                    <IconButton
                      aria-label={`Edit ${item.name}`}
                      onClick={() => navigate(`/consent/${item._id}`)}
                    >
                      <EditIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Preview">
                    <IconButton
                      aria-label={`Preview ${item.name}`}
                      onClick={() => openPreview(item)}
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    aria-label={`More actions for ${item.name}`}
                    onClick={(event) =>
                      setMenu({ anchor: event.currentTarget, row: item })
                    }
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              ),
            };
          })
        );

        const total = Number(payload?.meta?.total);
        setCount(Number.isFinite(total) ? total : list.length);
      })
      .catch((error) => {
        toast.error(apiMessage(error, "Could not load consent forms."));
      })
      .finally(() => setLoading(false));
  }, [
    pageNumber,
    rowsPerPage,
    debouncedSearch,
    statusFilter,
    specialityOptions,
    navigate,
    openPreview,
  ]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const create = async (values) => {
    setBusy(true);
    try {
      const payload = consentDetailsPayload(values);
      const response = await createConsentTemplate({
        ...payload,
        // The starter design, with the consent-specific fields the catalogue
        // suggests for this format (none for a custom one) — editable in the
        // builder, optional for the patient.
        design: withConsentFields(
          simpleStarterDesign(payload),
          presetFieldsFor(payload.type, meta?.consentFieldPresets)
        ),
      });
      toast.success("Consent created");
      setAddOpen(false);
      navigate(`/consent/${response.data.data.template._id}`);
    } catch (error) {
      toast.error(apiMessage(error, "Could not create this consent."));
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async (row) => {
    setBusy(true);
    try {
      const response = await duplicateConsentTemplate(row._id);
      toast.success(response.data.message || "Consent duplicated");
      setPageNumber(0);
      fetchTemplates();
      navigate(`/consent/${response.data.data.template._id}`);
    } catch (error) {
      toast.error(apiMessage(error, "Could not duplicate this consent."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget?._id) return;

    setBusy(true);
    try {
      await deleteConsentTemplate(deleteTarget._id);
      toast.success("Consent deleted");

      if (rows.length === 1 && pageNumber > 0) setPageNumber(pageNumber - 1);
      else fetchTemplates();
    } catch (error) {
      toast.error(apiMessage(error, "Could not delete this consent."));
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  const menuRow = menu?.row;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "1rem 0",
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6" sx={{ mr: "auto" }}>
          Consent Forms
        </Typography>

        <TextField
          size="small"
          label="Search consent forms"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          sx={{ minWidth: 260 }}
        />

        <TextField
          select
          size="small"
          label="Status"
          sx={{ minWidth: 150 }}
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setPageNumber(0);
          }}
        >
          <MenuItem value="">All</MenuItem>
          {CONSENT_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {statusMeta(status).label}
            </MenuItem>
          ))}
        </TextField>

        {/*
          No treatment mapping here: which treatment uses which consent is the
          clinic's own configuration (its app's Consent Mapping screen). DRCFO
          publishes the template and sets its Target / Usage, nothing more.
        */}
        <Button
          sx={{ ...btnStyle, bgcolor: "#194AF5", color: "white", ":hover": {} }}
          variant="contained"
          disabled={busy}
          onClick={() => setAddOpen(true)}
        >
          Add New Consent
        </Button>
      </Box>

      <Listtable
        tableheader={TABLE_HEADERS}
        tablebody={rows}
        rowsPerPage={rowsPerPage}
        page={pageNumber}
        handleChangePage={(event, newPage) => setPageNumber(newPage)}
        handleChangeRowsPerPage={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPageNumber(0);
        }}
        totalCount={count}
        dataKeyName={TABLE_KEYS}
        tag="consentTemplates"
      />

      <Menu
        open={Boolean(menu)}
        anchorEl={menu?.anchor}
        onClose={() => setMenu(null)}
      >
        <MenuItem
          onClick={() => {
            setMenu(null);
            duplicate(menuRow);
          }}
        >
          Duplicate
        </MenuItem>

        <MenuItem
          disabled={
            menuRow?.status !== "active" && !canActivate(menuRow || {})
          }
          onClick={async () => {
            setMenu(null);
            await changeStatus(menuRow, nextStatusFor(menuRow));
            fetchTemplates();
          }}
        >
          {menuRow?.status === "active" ? "Deactivate" : "Activate"}
          {menuRow?.status !== "active" && !canActivate(menuRow || {})
            ? ` — ${activateBlockedReason(menuRow || {})}`
            : ""}
        </MenuItem>

        <MenuItem
          onClick={() => {
            setMenu(null);
            setDeleteTarget(menuRow);
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <NewConsentDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreate={create}
        meta={meta}
        specialityOptions={specialityOptions}
        busy={busy}
      />

      {previewing ? (
        <ConsentPreviewDialog
          open
          onClose={() => setPreviewing(null)}
          template={previewing.template}
          design={previewing.design}
        />
      ) : null}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete consent?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deleteTarget?.name}" will stop being offered. Its saved versions
            are kept, so a consent already printed from it can still be
            explained.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={remove} color="error" disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={loading || busy} sx={{ zIndex: 1200, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default ConsentList;
